// A message's pictures, videos, GIFs and files. Each one has the same corner controls as a link preview: a chevron
// that collapses it to one line for me (remembered on this device), an × that removes it from the message for
// everyone (the author, owners and admins), and for the author an eye that marks a picture private.
// A private picture is drawn blurred and dark with a "View" button; looking is per viewer and stays on this device
// (viewerPrefs), so nobody else's view changes and the room never hears about it.
// A big picture with no smaller copy (too big for one, or not made yet) is never drawn from the original by itself: a
// placeholder of the same size shows its name and size, and loads it on a tap. Videos load nothing until played when
// they have a poster frame, and only their metadata (ranged requests) when they don't.
import { useEffect, useRef, useState } from "react";
import { attachmentRef, GIF_PLAYS, gifShouldStop, isVisualAttachment, type Attachment, type PhotoLines } from "@bloom/shared";
import { fileUrl, formatBytes, INLINE_ORIGINAL_MAX, isImage, isVideo, variantUrl } from "../upload.ts";
import { downloadable, gifBytesUrl, type Downloadable } from "../download.ts";
import { viewerPhoto } from "../viewerPhoto.ts";
import { revealed, useInSet } from "../viewerPrefs.ts";
import { ChevronIcon, CloseButton, RoundButton } from "./icons.tsx";
import { useCollapse } from "./collapse.ts";
import { removeWithUndo, usePendingRemoval } from "./undo.tsx";
import "./media.css";
import { StickerView } from "../customEmoji.tsx";
import { imgProps, keepImage } from "../imageMemory.ts";
import { useGifPlays } from "../gifPlays.ts";
import { CountedGif } from "./CountedGif.tsx";

/** How the lightbox shows what it opens. A GIF is shown as its animation (a looping muted video when it's
 *  video-format), scaled up from its known size to fill the screen. */
export interface OpenAs {
  /** A video to watch (an app record's attachment): controls and sound, not a looping GIF. */
  clip?: boolean;
  video?: boolean;
  width?: number;
  height?: number;
  /** What the lightbox's Download saves (downloadable() in download.ts). */
  download?: Downloadable;
  /** A picture's camera, settings and time, for the ⓘ panel (viewerPhoto.ts). Absent: no ⓘ button (#379). */
  photo?: PhotoLines;
  /**
   * Several items opened as one set (a post's pictures): swiped or arrowed through, starting at `index`. What's
   * opened is `gallery[index]`.
   */
  gallery?: ViewerItem[];
  index?: number;
}
/** One item in a set the lightbox pages through. */
export interface ViewerItem {
  src: string;
  original: string;
  as?: Omit<OpenAs, "gallery" | "index">;
}
/** Opens something in the lightbox. `original`: what "Open original" opens when what's shown is a smaller copy. */
export type OnOpen = (src: string, original?: string, as?: OpenAs) => void;

/** Attachments drawn as media or file cards (stickers are drawn on their own, by StickerView). */
type Shown = Exclude<Attachment, { kind: "sticker" }>;

/** Fits media into a box no bigger than maxW × maxH, keeping its shape. Known sizes mean no layout shift. */
export function fit(w: number | undefined, h: number | undefined, maxW: number, maxH: number) {
  if (!w || !h) return { width: maxW, height: Math.round(maxW * 0.66) };
  const s = Math.min(1, maxW / w, maxH / h);
  return { width: Math.round(w * s), height: Math.round(h * s) };
}

/** What to call an attachment in the collapsed line, the toast and labels. */
function nameOf(a: Shown) {
  return a.kind === "gif" ? a.title || "GIF" : a.name;
}
function kindOf(a: Shown) {
  return a.kind === "gif" ? "GIF" : isImage(a.type) ? "Image" : isVideo(a.type) ? "Video" : "File";
}

export function EyeOffIcon({ size = 16 }: { size?: number }) {
  return (
    <svg className="glyph" width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6.6 3.6A6.8 6.8 0 0 1 8 3.5c3.6 0 6 3.4 6.5 4.5-.2.5-.8 1.4-1.6 2.3M4.2 4.7C2.8 5.7 1.8 7.2 1.5 8c.5 1.1 2.9 4.5 6.5 4.5 1.3 0 2.5-.4 3.4-1" />
      <path d="M6.6 6.7a2 2 0 0 0 2.7 2.7M2 2l12 12" />
    </svg>
  );
}

export function EyeIcon({ size = 16 }: { size?: number }) {
  return (
    <svg className="glyph" width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1.5 8C2 6.9 4.4 3.5 8 3.5s6 3.4 6.5 4.5c-.5 1.1-2.9 4.5-6.5 4.5S2 9.1 1.5 8z" />
      <circle cx="8" cy="8" r="2" />
    </svg>
  );
}

/** Big originals someone chose to load in the chat, this page load (so scrolling away and back keeps them). */
const loadedOriginals = new Set<string>();

function ImageIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="9" cy="10" r="1.6" />
      <path d="m21 16-5-5-8 9" />
    </svg>
  );
}

/**
 * A GIF in a message. A video-format GIF plays the number of times this device chose (gifPlays.ts), then stops on its
 * last frame; a click then plays it again, and one that scrolls out of view and back counts from zero (#489). While it
 * plays, a click opens it large, where it loops. An image-format GIF from the picker (a GIF or WebP file; KLIPY's usual
 * format) follows the setting too (#589): CountedGif plays it that many times and holds its last frame, and a click plays
 * it again. Set to Forever, it is the plain picture, which the browser loops.
 */
function GifMedia({ a, onOpen, hidden }: { a: Extract<Shown, { kind: "gif" }>; onOpen: OnOpen; hidden: boolean }) {
  const box = fit(a.width, a.height, 300, 260);
  const label = a.title || "GIF";
  const plays = useGifPlays();
  const video = useRef<HTMLVideoElement>(null);
  const played = useRef(0);
  const inView = useRef(false);
  const [stopped, setStopped] = useState(false);
  const [replay, setReplay] = useState(0);
  const isVideo = a.format === "video";
  const count = GIF_PLAYS.find((c) => c.id === plays)?.plays ?? null;
  const counted = !isVideo && count !== null && !hidden;
  const holding = stopped && (isVideo || counted);

  /** Plays it from the start, counting from zero. */
  const restart = () => {
    if (!isVideo) {
      setReplay((n) => n + 1);
      return;
    }
    const v = video.current;
    if (!v) return;
    played.current = 0;
    setStopped(false);
    v.currentTime = 0;
    void v.play().catch(() => {});
  };

  // In view it plays; out of view it waits, and coming back counts from zero. A private GIF that is not looked at
  // stays still. Changing the setting starts the count again.
  useEffect(() => {
    const v = video.current;
    if (!v || hidden) return;
    if (typeof IntersectionObserver === "undefined") {
      restart();
      return;
    }
    const io = new IntersectionObserver((entries) => {
      const now = entries[entries.length - 1]!.isIntersecting;
      if (now === inView.current) return;
      inView.current = now;
      if (now) restart();
      else v.pause();
    });
    inView.current = false;
    io.observe(v);
    return () => io.disconnect();
  }, [hidden]);
  useEffect(() => {
    if (!hidden && (inView.current || typeof IntersectionObserver === "undefined")) restart();
  }, [plays]);

  const onEnded = () => {
    played.current += 1;
    if (gifShouldStop(plays, played.current)) setStopped(true);
    else void video.current?.play().catch(() => {});
  };

  const inner = (
    <>
      {isVideo ? (
        <video ref={video} src={a.url} width={box.width} height={box.height} loop={plays === "forever"} onEnded={onEnded} muted playsInline aria-label={hidden ? "Private GIF" : label} />
      ) : counted ? (
        <CountedGif url={a.url} fetchUrl={gifBytesUrl(a.url)} width={box.width} height={box.height} alt={label} plays={count} replay={replay} onStopped={setStopped} />
      ) : (
        <img src={a.url} width={box.width} height={box.height} alt={hidden ? "Private GIF" : label} {...imgProps(a.url)} />
      )}
      {!hidden && <span className="gif-badge">GIF</span>}
      {!hidden && holding && <span className="gif-replay" aria-hidden="true">▶</span>}
    </>
  );
  // Hidden: not a button until the person chooses to look.
  if (hidden) return <div className="media" style={box}>{inner}</div>;
  return (
    <button
      type="button"
      className={`media media-gif${holding ? " stopped" : ""}`}
      style={box}
      // A stopped GIF plays again; one being played opens large, like a picture.
      onClick={() => (holding ? restart() : onOpen(a.url, a.url, { video: isVideo, width: a.width, height: a.height, download: downloadable(a) ?? undefined }))}
      aria-label={holding ? `Play ${label} again` : `Open ${label}`}
    >
      {inner}
    </button>
  );
}

/**
 * A .gif file someone uploaded (#529). It plays the number of times this device chose (gifPlays.ts), then holds its last
 * frame (CountedGif); a click then plays it again, and one being played opens large, where it loops. Set to Forever, or
 * private and not looked at yet, it is the plain picture, which the browser loops.
 */
export function UploadedGif({ name, download, url, box, onOpen, hidden }: { name: string; download?: Downloadable; url: string; box: { width: number; height: number }; onOpen: OnOpen; hidden: boolean }) {
  const choice = useGifPlays();
  const plays = GIF_PLAYS.find((c) => c.id === choice)?.plays ?? null;
  const [stopped, setStopped] = useState(false);
  const [replay, setReplay] = useState(0);
  // A browser without ImageDecoder (Safari) holds a frame drawn from the playing picture instead (CountedGif).
  const counted = plays !== null && !hidden;
  const label = hidden ? "Private image" : name;
  const picture = counted
    ? <CountedGif url={url} width={box.width} height={box.height} alt={label} plays={plays} replay={replay} onStopped={setStopped} />
    : <img src={url} width={box.width} height={box.height} alt={label} {...imgProps(url)} />;
  if (hidden) return <div className="media" style={box}>{picture}</div>;
  const holding = counted && stopped;
  return (
    <button
      type="button"
      className={`media media-gif${holding ? " stopped" : ""}`}
      style={box}
      onClick={() => (holding ? setReplay((n) => n + 1) : onOpen(url, url, { download }))}
      aria-label={holding ? `Play ${name} again` : `Open ${name}`}
    >
      {picture}
      {holding && <span className="gif-replay" aria-hidden="true">▶</span>}
    </button>
  );
}

/** The picture, video, GIF or file card itself. `hidden`: private and not looked at yet, so blurred and inert. */
function Media({ a, onOpen, hidden }: { a: Shown; onOpen: OnOpen; hidden: boolean }) {
  if (a.kind === "gif") return <GifMedia a={a} onOpen={onOpen} hidden={hidden} />;
  const url = fileUrl(a.key);
  if (isImage(a.type)) {
    const box = fit(a.width, a.height, 400, 320);
    // A GIF file has no smaller copies (the server skips animated pictures) and follows the GIF plays setting.
    if (a.type === "image/gif" && a.size <= INLINE_ORIGINAL_MAX) return <UploadedGif name={a.name} download={downloadable(a) ?? undefined} url={url} box={box} onOpen={onOpen} hidden={hidden} />;
    // The server's smaller copies once they exist (the browser picks one for the screen); the original until then,
    // unless it's big (BigPicture).
    const small = a.variants?.find((v) => v.name === "w400.webp");
    const large = a.variants?.find((v) => v.name === "w1200.webp");
    if (!small && !large && a.size > INLINE_ORIGINAL_MAX) return <BigPicture a={a} url={url} box={box} onOpen={onOpen} hidden={hidden} />;
    const srcSet = small || large
      ? [small && `${url}/${small.name} ${small.width}w`, large ? `${url}/${large.name} ${large.width}w` : a.width && `${url} ${a.width}w`].filter(Boolean).join(", ")
      : "";
    const img = (
      <img
        src={large ? `${url}/${large.name}` : url}
        srcSet={srcSet || undefined}
        sizes={srcSet ? `${box.width}px` : undefined}
        width={box.width}
        height={box.height}
        alt={hidden ? "Private image" : a.name}
        {...imgProps(large ? `${url}/${large.name}` : url)}
        onLoad={(e) => keepImage(e.currentTarget.currentSrc)}
      />
    );
    // Hidden: not a button, so the lightbox can't open until the person chooses to look.
    return hidden ? (
      <div className="media" style={box}>{img}</div>
    ) : (
      <button type="button" className="media" style={box} onClick={() => onOpen(variantUrl(a, "w1200.webp") ?? url, url, { download: downloadable(a) ?? undefined, photo: viewerPhoto(a) })} aria-label={`Open ${a.name}`}>
        {img}
      </button>
    );
  }
  if (isVideo(a.type)) {
    const box = fit(a.width, a.height, 400, 320);
    // A poster frame before it plays, and an H.264 copy first for browsers that can't play the original (HEVC, .mov).
    // A new source needs a new element, hence the key.
    const playable = variantUrl(a, "h264.mp4");
    const poster = variantUrl(a, "poster.jpg");
    return (
      <video key={playable ? "h264" : "original"} className="media" poster={poster} width={box.width} height={box.height} controls={!hidden} preload={poster ? "none" : "metadata"}>
        {playable && <source src={playable} type="video/mp4" />}
        <source src={url} type={a.type === "video/quicktime" ? undefined : a.type} />
      </video>
    );
  }
  return (
    <a className="file-card" href={url} download={a.name}>
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
        <path d="M14 3v5h5" />
      </svg>
      <span style={{ minWidth: 0 }}>
        <span className="file-name">{a.name}</span>
        <span className="muted" style={{ fontSize: 12 }}>{formatBytes(a.size)}</span>
      </span>
    </a>
  );
}

/** A picture too heavy to draw unasked: same box, its name and size, and the original on a tap. */
function BigPicture({ a, url, box, onOpen, hidden }: { a: Extract<Shown, { kind: "file" }>; url: string; box: { width: number; height: number }; onOpen: OnOpen; hidden: boolean }) {
  const [loaded, setLoaded] = useState(() => loadedOriginals.has(a.key));
  if (loaded && !hidden) {
    if (a.type === "image/gif") return <UploadedGif name={a.name} download={downloadable(a) ?? undefined} url={url} box={box} onOpen={onOpen} hidden={hidden} />;
    return (
      <button type="button" className="media" style={box} onClick={() => onOpen(url, url, { download: downloadable(a) ?? undefined, photo: viewerPhoto(a) })} aria-label={`Open ${a.name}`}>
        <img src={url} width={box.width} height={box.height} alt={a.name} />
      </button>
    );
  }
  return (
    <button
      type="button"
      className="media media-placeholder"
      style={box}
      disabled={hidden}
      onClick={() => {
        loadedOriginals.add(a.key);
        setLoaded(true);
      }}
      aria-label={`Load ${a.name} (${formatBytes(a.size)})`}
    >
      <ImageIcon />
      <span className="media-placeholder-name">{hidden ? "Private image" : a.name}</span>
      <span className="media-placeholder-size">{formatBytes(a.size)} · {box.width < 200 ? "Tap to load" : "Too big to preview. Tap to load"}</span>
    </button>
  );
}

interface ItemProps {
  messageId: string;
  a: Shown;
  onOpen: OnOpen;
  /** Removes it for everyone (only for the author, owners and admins); sent after the undo toast runs out. */
  onRemove?: () => void;
  /** The author only: mark it private or not. */
  onSetPrivate?: (on: boolean) => void;
}

function Item({ messageId, a, onOpen, onRemove, onSetPrivate }: ItemProps) {
  const ref = attachmentRef(a);
  const visual = isVisualAttachment(a);
  const c = useCollapse(`${messageId}|${ref}`);
  const seen = useInSet(revealed, ref);
  const removing = usePendingRemoval(`att:${messageId}|${ref}`);
  if (removing) return null;
  const hidden = !!a.private && visual && !seen;
  const kind = kindOf(a).toLowerCase();
  const controls = (
    <div className={`att-controls${visual && !c.collapsed ? " over" : ""}`}>
      {onSetPrivate && visual && !c.collapsed && (
        <RoundButton
          size="sm"
          tone="media"
          className="private-toggle"
          aria-pressed={!!a.private}
          aria-label={a.private ? `Make ${kind} not private` : "Mark as private"}
          title={a.private ? "Private: click to show it to everyone" : "Mark as private"}
          onClick={() => onSetPrivate(!a.private)}
        >
          <EyeOffIcon size={14} />
        </RoundButton>
      )}
      <RoundButton
        size="sm"
        tone={visual && !c.collapsed ? "media" : "plain"}
        className="collapse-btn"
        aria-expanded={!c.collapsed}
        aria-label={c.collapsed ? `Expand ${kind}` : `Collapse ${kind}`}
        title={c.collapsed ? "Expand" : "Collapse"}
        onClick={c.toggle}
      >
        <ChevronIcon />
      </RoundButton>
      {onRemove && (
        <CloseButton
          size="sm"
          tone={visual && !c.collapsed ? "media" : "plain"}
          aria-label={`Remove ${kind} for everyone`}
          title="Remove for everyone"
          onClick={() => removeWithUndo(`att:${messageId}|${ref}`, `${kindOf(a)} removed`, onRemove)}
        />
      )}
    </div>
  );
  return (
    <div className={`att${visual ? " visual" : " file"}${c.collapsed ? " is-collapsed" : ""}${hidden ? " is-private" : ""}`} data-ref={ref}>
      {c.collapsed && (
        <div className="att-line">
          <span className="att-line-kind">{kindOf(a)}</span>
          <span className="att-line-name">{a.private && visual ? "Private" : nameOf(a)}</span>
        </div>
      )}
      <div className={c.bodyClass}>
        <div className="collapse-inner">
          {c.mounted && (
            <div className="att-media">
              <Media a={a} onOpen={onOpen} hidden={hidden} />
              {hidden && (
                // Like a spoiler, a tap anywhere on it reveals it (the button is there for keyboards and screen readers).
                <div className="private-cover" onClick={() => revealed.set(ref, true)}>
                  <button type="button" className="private-view" onClick={(ev) => { ev.stopPropagation(); revealed.set(ref, true); }}>
                    <EyeIcon size={16} /> View {kind === "gif" ? "GIF" : kind}
                  </button>
                </div>
              )}
              {!!a.private && visual && !hidden && (
                <span className="private-badge" title="Private: blurred until each person chooses to look">
                  <EyeOffIcon size={12} /> Private
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      {controls}
    </div>
  );
}

export function Attachments({ messageId, attachments, onOpen, onRemove, onSetPrivate }: {
  messageId: string;
  attachments: Attachment[];
  onOpen: OnOpen;
  onRemove?: (ref: string) => void;
  onSetPrivate?: (ref: string, on: boolean) => void;
}) {
  return (
    <div className="attachments">
      {attachments.map((a) => {
        const ref = attachmentRef(a);
        if (a.kind === "sticker") return <StickerView key={ref} id={a.id} name={a.name} />;
        return (
          <Item
            key={ref}
            messageId={messageId}
            a={a}
            onOpen={onOpen}
            onRemove={onRemove && (() => onRemove(ref))}
            onSetPrivate={onSetPrivate && ((on) => onSetPrivate(ref, on))}
          />
        );
      })}
    </div>
  );
}
